import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Trash2,
  CalendarDays,
  ListChecks,
  Rss,
  Copy,
  RotateCcw,
  CircleX,
  ArrowRightLeft,
  Users,
  Info,
  Check,
  X as XIcon,
  GripHorizontal,
} from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useTemplates,
  useSaveTemplates,
  useBookings,
  useUpdateBookingState,
  useReassignBooking,
  useIcalTokens,
  useRotateIcalToken,
  useRevokeIcalToken,
  type AvailabilityTemplate,
  type BookingRow,
  type IcalToken,
} from '@/lib/booking';
import { useMe } from '@/lib/brief';
import { env } from '@/lib/env';
import { useBrief } from '@/lib/brief';

export const Route = createFileRoute('/_app/calendar')({
  staticData: { crumb: 'Kalendarz wizyt' },
  component: CalendarPage,
});

function CalendarPage() {
  const { data: brief } = useBrief();
  const templates = useTemplates();
  const save = useSaveTemplates();
  const bookings = useBookings();
  const updateState = useUpdateBookingState();
  const reassign = useReassignBooking();

  const team = ((brief?.team ?? []) as Array<{ id: string; full_name: string }>) || [];

  const [items, setItems] = React.useState<AvailabilityTemplate[]>([]);
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [selectedDoctorId, setSelectedDoctorId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (templates.data && !dirty) setItems(templates.data);
  }, [templates.data, dirty]);

  React.useEffect(() => {
    if (selectedDoctorId) return;
    if (team.length > 0) setSelectedDoctorId(team[0]!.id);
  }, [team, selectedDoctorId]);

  const doctorItems = items.filter((t) => t.doctor_id === selectedDoctorId);
  const doctorBookings = (bookings.data ?? []).filter(
    (b) => b.doctor_id === selectedDoctorId,
  );
  const selectedDoctor = team.find((m) => m.id === selectedDoctorId);

  const bookingCountByDoctor = React.useMemo(() => {
    const map = new Map<string, { upcoming: number; total: number }>();
    const now = Math.floor(Date.now() / 1000);
    for (const b of bookings.data ?? []) {
      const slot = map.get(b.doctor_id) ?? { upcoming: 0, total: 0 };
      slot.total += 1;
      if (b.start_at >= now && b.state === 'confirmed') slot.upcoming += 1;
      map.set(b.doctor_id, slot);
    }
    return map;
  }, [bookings.data]);
  const blockCountByDoctor = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const t of items) map.set(t.doctor_id, (map.get(t.doctor_id) ?? 0) + 1);
    return map;
  }, [items]);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await save.mutateAsync(items);
      setDirty(false);
      setSavedStatus('saved');
      toast.success('Harmonogram zapisany');
    } catch {
      setSavedStatus('error');
      toast.error('Nie udało się zapisać harmonogramu');
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Kalendarz wizyt
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Tygodniowy harmonogram pracy zespołu. Wybierz lekarza, aby edytować jego rozkład:
          bloki pracy (kliknij siatkę, by dodać), wizyty i subskrypcję iCal.
        </p>
      </header>

      {team.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
              Aby uruchomić harmonogram, najpierw dodaj członków zespołu w sekcji „Treść strony
              → Zespół".
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* DOCTOR STRIP — horizontal selector above the suite */}
          <DoctorStrip
            team={team}
            selectedDoctorId={selectedDoctorId}
            onSelect={setSelectedDoctorId}
            blockCountByDoctor={blockCountByDoctor}
            bookingCountByDoctor={bookingCountByDoctor}
          />

          {/* PER-DOCTOR SUITE */}
          <div className="min-w-0 space-y-4">
            {selectedDoctor ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CalendarDays className="size-5" aria-hidden="true" /> Harmonogram pracy —
                      {' '}
                      <span className="text-text">{selectedDoctor.full_name}</span>
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Kliknij wolne miejsce w kolumnie, aby zaproponować blok. Potwierdź w
                      podglądzie. Istniejące bloki przeciągasz za górną/dolną krawędź lub środek
                      (snap co 15 minut).
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        onSave();
                      }}
                      className="space-y-4"
                    >
                      <WeekTimelineGrid
                        doctorItems={doctorItems}
                        items={items}
                        onChange={(next) => {
                          setItems(next);
                          setDirty(true);
                        }}
                        onAdd={(weekday, startMin) => {
                          const start = minToTime(startMin);
                          const endMin = Math.min(startMin + 60, 24 * 60);
                          const end = minToTime(endMin);
                          setItems([
                            ...items,
                            {
                              doctor_id: selectedDoctorId!,
                              weekday,
                              start_time: start,
                              end_time: end,
                              slot_minutes: 30,
                              service_slugs: null,
                              buffer_minutes: 0,
                            },
                          ]);
                          setDirty(true);
                        }}
                      />

                      <SaveBar
                        saving={save.isPending}
                        dirty={dirty}
                        status={savedStatus}
                        onCancel={() => {
                          setItems(templates.data ?? []);
                          setDirty(false);
                        }}
                      />
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <ListChecks className="size-5" aria-hidden="true" /> Wizyty u {selectedDoctor.full_name.split(' ')[0]}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Ostatnie 7 dni i terminy nadchodzące. Oznacz wizytę jako odbytą, no-show
                      lub przenieś do innego lekarza.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {bookings.isPending ? (
                      <div className="space-y-2 p-6">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    ) : doctorBookings.length === 0 ? (
                      <p className="p-6 text-sm text-text-muted">
                        Brak wizyt u tego lekarza. Pacjenci zaczną rezerwować po publikacji
                        strony i ustawieniu harmonogramu.
                      </p>
                    ) : (
                      <BookingsTable
                        bookings={doctorBookings}
                        team={team}
                        onState={(id, state, reason) => updateState.mutate({ id, state, reason })}
                        onReassign={(id, new_doctor_id, reason) =>
                          reassign.mutateAsync({ id, new_doctor_id, reason })
                        }
                        busy={updateState.isPending || reassign.isPending}
                      />
                    )}
                  </CardContent>
                </Card>

                <IcalSubscriptions team={[selectedDoctor]} />
              </>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <p className="text-center text-sm text-text-muted">
                    Wybierz lekarza powyżej, aby zobaczyć jego harmonogram i wizyty.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────── DoctorStrip ──────────────────── */

function DoctorStrip({
  team,
  selectedDoctorId,
  onSelect,
  blockCountByDoctor,
  bookingCountByDoctor,
}: {
  team: Array<{ id: string; full_name: string }>;
  selectedDoctorId: string | null;
  onSelect: (id: string) => void;
  blockCountByDoctor: Map<string, number>;
  bookingCountByDoctor: Map<string, { upcoming: number; total: number }>;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="size-4" aria-hidden="true" /> Zespół
          <span className="text-xs font-normal text-text-muted">
            ({team.length} {team.length === 1 ? 'osoba' : 'osób'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <div role="tablist" aria-label="Zespół" className="flex flex-wrap gap-2">
          {team.map((m) => {
            const active = m.id === selectedDoctorId;
            const blocks = blockCountByDoctor.get(m.id) ?? 0;
            const counts = bookingCountByDoctor.get(m.id);
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => onSelect(m.id)}
                className={cn(
                  'group flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                  active
                    ? 'border-accent/30 bg-accent/10 text-accent shadow-sm'
                    : 'border-border bg-surface text-text hover:bg-bg'
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase',
                    active ? 'bg-accent text-text-inverted' : 'bg-bg text-text-muted ring-1 ring-black/5'
                  )}
                >
                  {initialsOf(m.full_name)}
                </span>
                <span className="flex flex-col items-start">
                  <span className="font-medium leading-tight">{m.full_name}</span>
                  <span className="text-[11px] leading-tight text-text-subtle">
                    {blocks} {blocks === 1 ? 'blok' : 'bloki'}
                    {counts?.upcoming ? ` · ${counts.upcoming} wizyt` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function initialsOf(name: string): string {
  const parts = name.replace(/^(dr\.|lek\.|lek|prof\.|mgr)/i, '').trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '';
  return (first + last).toUpperCase();
}

/* ──────────────────── IcalSubscriptions ──────────────────── */

function IcalSubscriptions({ team }: { team: Array<{ id: string; full_name: string }> }) {
  const me = useMe();
  const tokens = useIcalTokens();
  const rotate = useRotateIcalToken();
  const revoke = useRevokeIcalToken();
  const [copied, setCopied] = React.useState<string | null>(null);

  const slug = me.data?.tenant.slug;
  const apiBase = env.apiBaseUrl.replace(/\/$/, '');
  const urlFor = (t: IcalToken) =>
    slug
      ? `${apiBase}/api/booking/${slug}/ical/${encodeURIComponent(t.doctor_id)}?token=${t.token}`
      : '';

  const byDoctor = new Map<string, IcalToken>();
  for (const t of tokens.data ?? []) byDoctor.set(t.doctor_id, t);

  const onCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      /* noop */
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Rss className="size-5" aria-hidden="true" /> Subskrypcje kalendarza (iCal)
        </CardTitle>
        <CardDescription className="text-sm">
          Każdy lekarz dostaje unikalny adres URL, który można dodać jako subskrybowany
          kalendarz w Google Calendar, Apple Calendar lub Outlooku — synchronizacja
          jednostronna (tylko-do-odczytu), odświeżana ~co godzinę. Rotacja unieważnia
          poprzedni link.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {team.length === 0 ? (
          <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
            Najpierw dodaj zespół w Treści strony → Zespół.
          </p>
        ) : (
          <ul role="list" className="space-y-2">
            {team.map((doctor) => {
              const tok = byDoctor.get(doctor.id);
              return (
                <li
                  key={doctor.id}
                  className="rounded-md bg-surface p-4 ring-1 ring-black/5"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-display text-base font-semibold text-text">
                      {doctor.full_name || doctor.id}
                    </p>
                    <p className="text-xs text-text-subtle">
                      {tok
                        ? tok.last_used_at
                          ? `Ostatnie pobranie: ${new Date(tok.last_used_at * 1000).toLocaleString('pl-PL')}`
                          : 'Jeszcze nie pobrane'
                        : 'Brak aktywnej subskrypcji'}
                    </p>
                  </div>

                  {tok ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        readOnly
                        value={urlFor(tok)}
                        onFocus={(e) => e.currentTarget.select()}
                        className="h-9 flex-1 min-w-0 truncate rounded-md bg-bg px-3 font-mono text-xs ring-1 ring-border"
                      />
                      <Button type="button" variant="secondary" size="sm" onClick={() => onCopy(urlFor(tok), doctor.id)}>
                        <Copy className="size-3.5" aria-hidden="true" />
                        {copied === doctor.id ? 'Skopiowano' : 'Kopiuj'}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => rotate.mutate(doctor.id)} disabled={rotate.isPending}>
                        <RotateCcw className="size-3.5" aria-hidden="true" /> Rotuj
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => revoke.mutate(doctor.id)} disabled={revoke.isPending}>
                        <CircleX className="size-3.5" aria-hidden="true" /> Usuń
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => rotate.mutate(doctor.id)} disabled={rotate.isPending}>
                        <Rss className="size-3.5" aria-hidden="true" /> Wygeneruj link subskrypcji
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────── BookingsTable + ReassignDialog ──────────────────── */

function BookingsTable({
  bookings,
  team,
  onState,
  onReassign,
  busy,
}: {
  bookings: BookingRow[];
  team: Array<{ id: string; full_name: string }>;
  onState: (id: string, state: 'cancelled' | 'no_show' | 'completed', reason?: string) => void;
  onReassign: (id: string, new_doctor_id: string, reason?: string) => Promise<unknown>;
  busy: boolean;
}) {
  const doctorName = (id: string) => team.find((t) => t.id === id)?.full_name ?? id;
  const [reassignTarget, setReassignTarget] = React.useState<BookingRow | null>(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[56rem] text-sm">
        <thead className="bg-surface text-left text-xs uppercase tracking-wide text-text-subtle">
          <tr>
            <th className="px-4 py-3 font-medium">Termin</th>
            <th className="px-4 py-3 font-medium">Pacjent</th>
            <th className="px-4 py-3 font-medium">Lekarz</th>
            <th className="px-4 py-3 font-medium">Usługa</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium text-right">Akcje</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-bg">
          {bookings.map((b) => {
            const date = new Date(b.start_at * 1000);
            const dateStr = date.toLocaleString('pl-PL', {
              timeZone: 'Europe/Warsaw',
              dateStyle: 'medium',
              timeStyle: 'short',
            });
            return (
              <tr key={b.id}>
                <td className="px-4 py-3 text-text">{dateStr}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-text">{b.patient_name}</p>
                  <p className="text-xs text-text-subtle font-mono">{b.patient_phone}</p>
                </td>
                <td className="px-4 py-3 text-text-muted">{doctorName(b.doctor_id)}</td>
                <td className="px-4 py-3 text-text-muted">{b.service_slug ?? '—'}</td>
                <td className="px-4 py-3">
                  <Badge
                    variant={
                      b.state === 'confirmed'
                        ? 'success'
                        : b.state === 'no_show'
                        ? 'destructive'
                        : b.state === 'cancelled'
                        ? 'default'
                        : 'soft'
                    }
                  >
                    {b.state}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  {b.state === 'confirmed' ? (
                    <div className="inline-flex items-center gap-1">
                      <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onState(b.id, 'completed')}>
                        Odbyta
                      </Button>
                      <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onState(b.id, 'no_show')}>
                        No-show
                      </Button>
                      {team.length > 1 ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          title="Przenieś wizytę do innego lekarza"
                          onClick={() => setReassignTarget(b)}
                        >
                          <ArrowRightLeft className="size-3.5" aria-hidden="true" /> Przenieś
                        </Button>
                      ) : null}
                      <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onState(b.id, 'cancelled', 'Anulowano przez recepcję')}>
                        Odwołaj
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-text-subtle">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {reassignTarget ? (
        <ReassignDialog
          booking={reassignTarget}
          team={team}
          busy={busy}
          onClose={() => setReassignTarget(null)}
          onConfirm={async (new_doctor_id, reason) => {
            await onReassign(reassignTarget.id, new_doctor_id, reason);
            setReassignTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ReassignDialog({
  booking,
  team,
  busy,
  onClose,
  onConfirm,
}: {
  booking: BookingRow;
  team: Array<{ id: string; full_name: string }>;
  busy: boolean;
  onClose: () => void;
  onConfirm: (new_doctor_id: string, reason?: string) => Promise<void>;
}) {
  const candidates = team.filter((t) => t.id !== booking.doctor_id);
  const [doctorId, setDoctorId] = React.useState(candidates[0]?.id ?? '');
  const [reason, setReason] = React.useState('');

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Przenieś wizytę do innego lekarza</DialogTitle>
          <DialogDescription>
            {booking.patient_name} —{' '}
            {new Date(booking.start_at * 1000).toLocaleString('pl-PL', {
              timeZone: 'Europe/Warsaw',
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Nowy lekarz" htmlFor="reassign-doctor" required>
            <Select id="reassign-doctor" value={doctorId} onChange={(e) => setDoctorId(e.currentTarget.value)}>
              {candidates.length === 0 ? (
                <option value="">Brak dostępnych lekarzy</option>
              ) : (
                candidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.full_name}
                  </option>
                ))
              )}
            </Select>
          </Field>

          <Field label="Powód (opcjonalnie)" htmlFor="reassign-reason">
            <Input id="reassign-reason" value={reason} onChange={(e) => setReason(e.currentTarget.value)} placeholder="np. dr. nieobecny" />
          </Field>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button
            type="button"
            disabled={!doctorId || busy}
            onClick={async () => {
              try {
                await onConfirm(doctorId, reason || undefined);
              } catch (e) {
                const msg = (e as Error).message ?? 'reassign_failed';
                toast.error(
                  msg === 'destination_slot_taken'
                    ? 'Nowy lekarz ma już wizytę w tym terminie.'
                    : 'Nie udało się przenieść wizyty. Spróbuj ponownie.'
                );
              }
            }}
          >
            Przenieś wizytę
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── WeekTimelineGrid: 0–24h × 7-day visual grid ──────────────────── */

const TIMELINE_DAYS = [
  { v: 0, label: 'Pon' },
  { v: 1, label: 'Wt' },
  { v: 2, label: 'Śr' },
  { v: 3, label: 'Czw' },
  { v: 4, label: 'Pt' },
  { v: 5, label: 'Sob' },
  { v: 6, label: 'Niedz' },
] as const;

const HOUR_PX = 48;
const SNAP_MIN = 15;
const GRID_START_MIN = 0;
const GRID_END_MIN = 24 * 60;
const HOUR_COUNT = (GRID_END_MIN - GRID_START_MIN) / 60;

function timeToMin(s: string): number {
  const [h, m] = s.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}
function minToTime(m: number): string {
  const h = Math.max(0, Math.min(23, Math.floor(m / 60)));
  const mm = Math.max(0, Math.min(59, m % 60));
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function snap(m: number, snapTo = SNAP_MIN) {
  return Math.round(m / snapTo) * snapTo;
}

function WeekTimelineGrid({
  doctorItems,
  items,
  onChange,
  onAdd,
}: {
  doctorItems: AvailabilityTemplate[];
  items: AvailabilityTemplate[];
  onChange: (next: AvailabilityTemplate[]) => void;
  onAdd: (weekday: number, startMin: number) => void;
}) {
  const totalHeight = HOUR_COUNT * HOUR_PX;
  const hourMarks = Array.from({ length: HOUR_COUNT + 1 }, (_, i) => GRID_START_MIN + i * 60);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // On first mount, scroll to 7:00 so the user lands on a useful window
  // without losing the 24h scrollability for 24/7 clinics.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.dataset.scrolled === '1') return;
    el.scrollTop = 7 * HOUR_PX;
    el.dataset.scrolled = '1';
  }, []);

  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] ring-1 ring-black/5">
      {/* Header row: day labels (sticky over scroll body) */}
      <div
        className="grid border-b border-border bg-surface text-sm font-semibold uppercase tracking-wide text-text"
        style={{ gridTemplateColumns: '4rem repeat(7, minmax(0, 1fr))' }}
      >
        <div className="px-2 py-3" aria-hidden="true" />
        {TIMELINE_DAYS.map((d) => (
          <div key={d.v} className="border-l border-border px-2 py-3 text-center">
            {d.label}
          </div>
        ))}
      </div>

      {/* Body: 0–24h scrollable */}
      <div
        ref={scrollRef}
        className="relative overflow-y-auto bg-bg"
        style={{ maxHeight: 'min(70vh, 720px)' }}
      >
        <div
          className="relative"
          style={{ height: totalHeight, gridTemplateColumns: '4rem repeat(7, minmax(0, 1fr))' }}
        >
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: '4rem repeat(7, minmax(0, 1fr))' }}>
            {/* Hour gutter */}
            <div className="relative">
              {hourMarks.slice(0, -1).map((m, i) => (
                <div
                  key={i}
                  className="absolute right-2 -translate-y-2 text-xs font-mono tabular-nums text-text-muted"
                  style={{ top: i * HOUR_PX }}
                >
                  {minToTime(m)}
                </div>
              ))}
            </div>

            {/* 7 day columns */}
            {TIMELINE_DAYS.map((d) => (
              <DayColumn
                key={d.v}
                weekday={d.v}
                weekdayLabel={d.label}
                blocks={doctorItems.filter((t) => t.weekday === d.v)}
                items={items}
                onChange={onChange}
                onAdd={onAdd}
              />
            ))}
          </div>
        </div>
      </div>

      <p className="flex items-center gap-2 border-t border-border bg-surface px-3 py-2 text-xs text-text-muted">
        <Info className="size-3.5 shrink-0" aria-hidden="true" />
        Kliknij wolne miejsce w kolumnie, by zaproponować blok — potwierdź w podglądzie. Aby
        zmienić istniejący blok, przeciągnij górę/dół (zmiana godzin) lub środek (przesunięcie).
      </p>
    </div>
  );
}

/* ──────────────────── DayColumn ──────────────────── */

function DayColumn({
  weekday,
  weekdayLabel,
  blocks,
  items,
  onChange,
  onAdd,
}: {
  weekday: number;
  weekdayLabel: string;
  blocks: AvailabilityTemplate[];
  items: AvailabilityTemplate[];
  onChange: (next: AvailabilityTemplate[]) => void;
  onAdd: (weekday: number, startMin: number) => void;
}) {
  const colRef = React.useRef<HTMLDivElement | null>(null);
  const [ghost, setGhost] = React.useState<{ startMin: number; endMin: number; x: number; y: number } | null>(null);

  const totalHeight = HOUR_COUNT * HOUR_PX;
  const hourLines = Array.from({ length: HOUR_COUNT }, (_, i) => i);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-block]')) return;
    const rect = colRef.current!.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const rawMin = GRID_START_MIN + (y / HOUR_PX) * 60;
    const startMin = Math.max(GRID_START_MIN, Math.min(GRID_END_MIN - 30, snap(rawMin, 30)));
    const endMin = Math.min(GRID_END_MIN, startMin + 60);
    setGhost({ startMin, endMin, x: e.clientX, y: e.clientY });
  };

  const cancelGhost = () => setGhost(null);
  const confirmGhost = () => {
    if (!ghost) return;
    onAdd(weekday, ghost.startMin);
    setGhost(null);
  };

  return (
    <div
      ref={colRef}
      onClick={handleClick}
      className="relative border-l border-border hover:bg-accent/[0.03]"
      style={{ height: totalHeight, cursor: 'copy' }}
    >
      {/* Hour grid lines */}
      {hourLines.map((i) => (
        <div
          key={i}
          className={cn(
            'pointer-events-none absolute inset-x-0',
            i % 2 === 0 ? 'border-t border-border/60' : 'border-t border-border/30'
          )}
          style={{ top: i * HOUR_PX }}
        />
      ))}

      {/* Existing blocks */}
      {blocks.map((t) => {
        const idx = items.indexOf(t);
        return (
          <BlockTile
            key={`${t.weekday}:${t.start_time}:${idx}`}
            template={t}
            idx={idx}
            items={items}
            onChange={onChange}
            weekdayLabel={weekdayLabel}
          />
        );
      })}

      {/* Ghost preview popover (no auto-add) */}
      {ghost ? (
        <GhostPreview
          weekday={weekday}
          weekdayLabel={weekdayLabel}
          startMin={ghost.startMin}
          endMin={ghost.endMin}
          onCancel={cancelGhost}
          onConfirm={confirmGhost}
        />
      ) : null}
    </div>
  );
}

/* ──────────────────── GhostPreview ──────────────────── */

function GhostPreview({
  weekdayLabel,
  startMin,
  endMin,
  onCancel,
  onConfirm,
}: {
  weekday: number;
  weekdayLabel: string;
  startMin: number;
  endMin: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const top = ((startMin - GRID_START_MIN) / 60) * HOUR_PX;
  const height = Math.max(36, ((endMin - startMin) / 60) * HOUR_PX);
  return (
    <div
      className="absolute inset-x-1 z-20 rounded-md border-2 border-dashed border-accent/60 bg-accent/15 shadow-md"
      style={{ top, height }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex h-full flex-col items-start justify-between gap-1 p-1.5 text-text">
        <div className="leading-tight">
          <p className="text-xs font-mono tabular-nums">{minToTime(startMin)}–{minToTime(endMin)}</p>
          <p className="text-[11px] text-text-muted">{weekdayLabel}</p>
        </div>
        <div className="flex w-full gap-1">
          <Button type="button" size="sm" variant="primary" className="h-7 flex-1 px-2 text-xs" onClick={onConfirm}>
            <Check className="size-3.5" aria-hidden="true" /> Dodaj
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onCancel} aria-label="Anuluj">
            <XIcon className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── BlockTile (drag-resize / drag-move + popover) ──────────────────── */

type DragMode = 'top' | 'bottom' | 'body';

function BlockTile({
  template,
  idx,
  items,
  onChange,
  weekdayLabel,
}: {
  template: AvailabilityTemplate;
  idx: number;
  items: AvailabilityTemplate[];
  onChange: (next: AvailabilityTemplate[]) => void;
  weekdayLabel: string;
}) {
  const [draftStart, setDraftStart] = React.useState<number | null>(null);
  const [draftEnd, setDraftEnd] = React.useState<number | null>(null);
  const dragRef = React.useRef<{ mode: DragMode; pointerStartY: number; originalStart: number; originalEnd: number; moved: boolean } | null>(null);

  const startMin = draftStart ?? timeToMin(template.start_time);
  const endMin = draftEnd ?? timeToMin(template.end_time);
  const top = ((startMin - GRID_START_MIN) / 60) * HOUR_PX;
  const height = Math.max(28, ((endMin - startMin) / 60) * HOUR_PX);

  const beginDrag = (mode: DragMode) => (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      pointerStartY: e.clientY,
      originalStart: timeToMin(template.start_time),
      originalEnd: timeToMin(template.end_time),
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = e.clientY - d.pointerStartY;
    if (Math.abs(dy) < 3 && !d.moved) return;
    d.moved = true;
    const dMin = snap((dy / HOUR_PX) * 60);
    let s = d.originalStart;
    let en = d.originalEnd;
    if (d.mode === 'top') {
      s = Math.max(GRID_START_MIN, Math.min(en - SNAP_MIN, d.originalStart + dMin));
    } else if (d.mode === 'bottom') {
      en = Math.max(s + SNAP_MIN, Math.min(GRID_END_MIN, d.originalEnd + dMin));
    } else {
      const duration = d.originalEnd - d.originalStart;
      s = Math.max(GRID_START_MIN, Math.min(GRID_END_MIN - duration, d.originalStart + dMin));
      en = s + duration;
    }
    setDraftStart(s);
    setDraftEnd(en);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (d.moved && draftStart !== null && draftEnd !== null) {
      const next = [...items];
      next[idx] = {
        ...template,
        start_time: minToTime(draftStart),
        end_time: minToTime(draftEnd),
      };
      onChange(next);
    }
    setDraftStart(null);
    setDraftEnd(null);
    dragRef.current = null;
  };

  const onUpdate = (patch: Partial<AvailabilityTemplate>) => {
    const next = [...items];
    next[idx] = { ...template, ...patch };
    onChange(next);
  };
  const onDelete = () => onChange(items.filter((_, i) => i !== idx));

  // Only open the popover from a real (non-drag) click on the body region.
  const [popOpen, setPopOpen] = React.useState(false);

  return (
    <Popover open={popOpen} onOpenChange={setPopOpen}>
      <PopoverTrigger asChild>
        <div
          data-block=""
          role="button"
          tabIndex={0}
          aria-label={`Blok ${template.start_time}–${template.end_time}, ${weekdayLabel}, slot ${template.slot_minutes} min`}
          className="absolute inset-x-1 overflow-hidden rounded-md bg-accent text-text-inverted ring-1 ring-accent/40 shadow-sm transition-shadow hover:shadow-md"
          style={{ top, height, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            // If the user just dragged, don't open.
            if (dragRef.current?.moved) return;
            setPopOpen((o) => !o);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPopOpen(true);
            }
          }}
        >
          {/* Top resize handle */}
          <div
            role="separator"
            aria-label="Zmień godzinę rozpoczęcia"
            className="absolute inset-x-0 top-0 z-10 h-2.5 cursor-ns-resize bg-white/0 hover:bg-white/20"
            onPointerDown={beginDrag('top')}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />

          {/* Body / move handle */}
          <div
            className="absolute inset-x-0 top-2.5 bottom-2.5 z-0 flex cursor-move flex-col items-start justify-between gap-0.5 px-2 py-1 text-left"
            onPointerDown={beginDrag('body')}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <p className="text-sm font-mono tabular-nums leading-tight">
              {minToTime(startMin)}–{minToTime(endMin)}
            </p>
            {height >= 56 ? (
              <p className="text-xs leading-tight opacity-85">{template.slot_minutes}-min sloty</p>
            ) : null}
            {height >= 48 ? (
              <div className="self-center opacity-50" aria-hidden="true">
                <GripHorizontal className="size-3" />
              </div>
            ) : null}
          </div>

          {/* Bottom resize handle */}
          <div
            role="separator"
            aria-label="Zmień godzinę zakończenia"
            className="absolute inset-x-0 bottom-0 z-10 h-2.5 cursor-ns-resize bg-white/0 hover:bg-white/20"
            onPointerDown={beginDrag('bottom')}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-3" align="start" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
          Blok harmonogramu
        </p>
        <Field label="Dzień">
          <Select value={String(template.weekday)} onChange={(e) => onUpdate({ weekday: parseInt(e.target.value, 10) })}>
            {TIMELINE_DAYS.map((d) => (
              <option key={d.v} value={d.v}>{d.label}</option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Od">
            <Input type="time" value={template.start_time} onChange={(e) => onUpdate({ start_time: e.target.value })} />
          </Field>
          <Field label="Do">
            <Input type="time" value={template.end_time} onChange={(e) => onUpdate({ end_time: e.target.value })} />
          </Field>
        </div>
        <Field label="Długość slotu (min)" hint="Co ile minut pacjent może wybrać godzinę">
          <Input
            type="number"
            min={5}
            max={180}
            step={5}
            value={template.slot_minutes}
            onChange={(e) => onUpdate({ slot_minutes: Math.max(5, Math.min(180, Number(e.target.value))) })}
          />
        </Field>
        <div className="flex justify-end gap-2 border-t border-border pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="size-3.5" aria-hidden="true" /> Usuń
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
